// @ts-nocheck
import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles, Box, FormControlLabel, Switch, Button, Icon } from '@material-ui/core';
import { GridOverlay, GridToolbarContainer } from '@material-ui/data-grid';
import { DataTable, EmptyState } from '../../../../../../common/components';
import { useDialog } from '../../../../../../common/hooks';
import { DateUtils } from '../../../../../../common/utils';
import ContractForm from '../../../common/ContractForm';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    height: '100%',
    minHeight: '500px',
    '& .MuiDataGrid-root': {
      border: '0',
    },
  },
  dialog: {
    padding: '0',
    marginBottom: '0',
  },
  dialogContent: {
    display: 'flex',
    maxWidth: '750px',
    overflow: 'hidden',
  },
  actions: {
    display: 'flex',
    '& > .MuiButton-root': {
      marginRight: theme.spacing(1),
      '&:last-child': {
        marginRight: '0',
      },
    },
  },
}));

function CompanyContractsTab(props) {
  const [t] = useTranslation();
  const { showDialog } = useDialog();
  const classes = useStyles();

  const {
    company,
    products,
    showActive,
    onSelectContract,
    onCreateContract,
    onFilterChange,
    actionsDisabled,
  } = props;

  const getColumnDefs = () => [
    {
      field: 'id',
      headerName: t('company.companyContractId'),
      width: 175,
    },
    {
      field: 'productId',
      headerName: t('company.companyContractProductId'),
      width: 175,
    },
    {
      field: 'productName',
      headerName: t('company.companyContractProductName'),
      width: 350,
      flex: 1,
    },
    {
      field: 'status',
      headerName: t('company.companyContractStatus'),
      width: 175,
    },
    {
      field: 'createdAt',
      headerName: t('company.companyContractCreated'),
      width: 200,
      type: 'dateTime',
      valueFormatter: params => DateUtils.getDateTime(params.value),
    },
    {
      field: 'expiredOn',
      headerName: t('company.companyContractExpired'),
      width: 200,
      type: 'dateTime',
      valueFormatter: params => DateUtils.getDateTime(params.value),
    },
  ];

  const getRowData = () =>
    _.filter(company.contracts, contract => !showActive || contract.status === 'Aktiv');

  const handleShowCreateContractDialog = () => {
    showDialog({
      title: t('contract.contractCreate'),
      content: params => (
        <Box className={classes.dialogContent}>
          <ContractForm
            company={company}
            products={products}
            onSave={params.onConfirm}
            onCancel={params.onCancel}
            saveButtonText={t('common.create')}
          />
        </Box>
      ),
      onConfirm: onCreateContract,
      className: classes.dialog,
      throwOnError: true,
      hideFooter: true,
    });
  };

  const renderToolbar = () => (
    <GridToolbarContainer>
      <FormControlLabel
        className={classes.switch}
        control={
          <Switch
            id="CompanyContractsTab-showActive"
            color="primary"
            checked={showActive}
            onChange={onFilterChange}
            disabled={actionsDisabled}
          />
        }
        label={t('common.onlyActive')}
      />
      <Button
        id="CompanyContractsTab-createContract"
        variant="outlined"
        color="primary"
        startIcon={<Icon>assignment</Icon>}
        onClick={handleShowCreateContractDialog}
        disabled={actionsDisabled || !products}
      >
        {t('contract.contractCreate')}
      </Button>
    </GridToolbarContainer>
  );

  const renderNoRowsOverlay = () => (
    <GridOverlay>
      <EmptyState variant="empty" message={t('company.companyContractsEmpty')} />
    </GridOverlay>
  );

  return (
    <Box id="CompanyContractsTab-container" className={classes.container}>
      <DataTable
        id="CompanyContractsTab-table"
        components={{
          Toolbar: renderToolbar,
          NoRowsOverlay: renderNoRowsOverlay,
        }}
        columns={getColumnDefs()}
        rows={getRowData()}
        onRowClick={onSelectContract}
        pageSize={company.contracts?.length}
        paginationMode="client"
        pagination
      />
    </Box>
  );
}

CompanyContractsTab.defaultProps = {
  products: undefined,
};

CompanyContractsTab.propTypes = {
  company: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    cvrNumber: PropTypes.number,
    eanNumber: PropTypes.string,
    status: PropTypes.string,
    segment: PropTypes.string,
    department: PropTypes.string,
    address: PropTypes.string,
    zipcode: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    phone: PropTypes.string,
    notes: PropTypes.string,
    invoiceEmail: PropTypes.string,
    contactName: PropTypes.string,
    contactPhone: PropTypes.string,
    contactEmail: PropTypes.string,
    contracts: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  products: PropTypes.arrayOf(PropTypes.object),
  showActive: PropTypes.bool.isRequired,
  actionsDisabled: PropTypes.bool.isRequired,
  onSelectContract: PropTypes.func.isRequired,
  onCreateContract: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
};

export default CompanyContractsTab;
